import * as React from "react";
import { Check, ChevronDown, CircleX, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui";
import { SelectProps } from "./types";

const Select: React.FC<SelectProps> = ({
  placeholder = "Select",
  searchPlaceholder = "Search...",
  noDataPlaceholder = "No data found",
  options = [],
  value,
  onChange,
  disabled,
  className,
  multiSelect = false,
  onClear,
  showSearch = true,
  showChip = false,
}) => {
  const [open, setOpen] = React.useState(false);

  const selectedValues = React.useMemo(
    () => (Array.isArray(value) ? value : value ? [value] : []),
    [value]
  );

  const selectedLabels = React.useMemo(
    () =>
      selectedValues
        .map((val) => options.find((opt) => opt.value === val)?.label)
        .filter(Boolean) as string[],
    [selectedValues, options]
  );

  const renderDisplayValue = () => {
    if (!selectedLabels.length || showChip) {
      return <span className="text-g-400">{placeholder}</span>;
    }
    return selectedLabels.join(", ");
  };

  const toggleMultiValue = (selectedValue: string) => {
    const updated = selectedValues.includes(selectedValue)
      ? selectedValues.filter((v) => v !== selectedValue)
      : [...selectedValues, selectedValue];

    onChange(updated as any);
  };

  const handleSelect = (selectedValue: string) => {
    if (multiSelect) {
      toggleMultiValue(selectedValue);
    } else {
      onChange(selectedValue);
      setOpen(false);
    }
  };

  const isSelected = (val: string) =>
    multiSelect ? selectedValues.includes(val) : value === val;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          asChild
          disabled={disabled}
          className={cn("h-[40px] w-full", className)}
        >
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between font-normal border-gray-200"
          >
            <span className="truncate text-[#2b333f]">
              {renderDisplayValue()}
            </span>

            {disabled ? (
              <></>
            ) : onClear && selectedValues.length ? (
              <CircleX
                className="opacity-50 cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClear();
                }}
              />
            ) : (
              <ChevronDown className="opacity-50" />
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] no-scrollbar">
          <Command>
            {showSearch && (
              <CommandInput placeholder={searchPlaceholder} className="h-9" />
            )}

            <CommandList className="max-h-60 overflow-auto no-scrollbar" onWheel={(e) => e.stopPropagation()}>
              <CommandEmpty>{noDataPlaceholder}</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem
                    key={opt.value}
                    value={opt.value}
                    keywords={[opt.label]}
                    onSelect={() => handleSelect(opt.value)}
                    disabled={opt.disabled}
                  >
                    {opt.label}
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        isSelected(opt.value) ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Chips */}
      {showChip && multiSelect && selectedLabels.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedLabels.map((label, idx) => (
            <div
              key={idx}
              className="flex items-center gap-1 bg-primary-100 text-brown-300 px-2 py-1 rounded-full text-sm"
            >
              <span>{label}</span>
              <X
                className="w-4 cursor-pointer"
                onClick={() => toggleMultiValue(selectedValues[idx])}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default Select;
